import React, { useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import allLocales from '@fullcalendar/core/locales-all';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import DashboardPanel from './DashboardPanel';
import { CATEGORY_ORDER, CATEGORY_STYLES, formatDateTimeRange, formatFee, formatTime, getTrainingCategory } from '../utils/dashboardUtils';

const DetailRow = ({ icon, label, value }) => (
    <div className="flex align-items-start gap-2 text-sm">
        <i className={`pi ${icon} mt-1`} style={{ fontSize: '0.85rem', color: '#667085' }} />
        <span style={{ color: '#667085', minWidth: '6.5rem' }}>{label}</span>
        <span className="font-medium" style={{ color: '#101828' }}>
            {value}
        </span>
    </div>
);

const TrainingCalendar = ({ events, viewMonth, onMonthChange, isFetching }) => {
    const { th, getCurrentLocale, getCurrentLanguageCode } = useLanguage();
    const locale = getCurrentLocale();
    const calendarRef = useRef(null);
    const overlayRef = useRef(null);
    const [selected, setSelected] = useState(null);

    const getApi = () => calendarRef.current?.getApi();
    const monthTitle = new Date(viewMonth.year, viewMonth.month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

    const renderEvent = (arg) => {
        const { item, category } = arg.event.extendedProps;
        const style = CATEGORY_STYLES[category];
        return (
            <div className="lms-dashboard-calendar__event" style={{ backgroundColor: style.background, color: style.color }}>
                <span className="font-semibold">{formatTime(item.startTime)}</span> {item.trainingName}
            </div>
        );
    };

    const handleEventClick = (info) => {
        info.jsEvent.preventDefault();
        setSelected(info.event.extendedProps.item);
        overlayRef.current?.toggle(info.jsEvent, info.el);
    };

    const legend = (
        <div className="flex align-items-center gap-3 flex-wrap">
            {CATEGORY_ORDER.map((category) => (
                <span key={category} className="flex align-items-center gap-2 text-sm" style={{ color: '#344054' }}>
                    <span className="border-circle inline-block" style={{ width: '0.6rem', height: '0.6rem', backgroundColor: CATEGORY_STYLES[category].dot }} />
                    {th(`lmsDashboard.calendar.categories.${category}`)}
                </span>
            ))}
        </div>
    );

    const selectedStyle = selected ? CATEGORY_STYLES[getTrainingCategory(selected.trainingType)] : null;

    return (
        <DashboardPanel title={th('lmsDashboard.calendar.title')}>
            <div className="flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
                <div className="flex align-items-center gap-2">
                    <Button icon="pi pi-chevron-left" outlined severity="secondary" size="small" onClick={() => getApi()?.prev()} aria-label={th('lmsDashboard.calendar.previous')} />
                    <span className="flex align-items-center gap-2 px-3 py-2 font-semibold" style={{ border: '1px solid #D0D5DD', borderRadius: '0.5rem', color: '#101828', minWidth: '11rem', textTransform: 'capitalize' }}>
                        <i className="pi pi-calendar" style={{ color: '#475467' }} />
                        {monthTitle}
                    </span>
                    <Button icon="pi pi-chevron-right" outlined severity="secondary" size="small" onClick={() => getApi()?.next()} aria-label={th('lmsDashboard.calendar.next')} />
                    <Button label={th('lmsDashboard.calendar.today')} outlined severity="secondary" size="small" onClick={() => getApi()?.today()} />
                    {isFetching && <ProgressSpinner style={{ width: '1.25rem', height: '1.25rem' }} strokeWidth="6" />}
                </div>
                {legend}
            </div>

            <div className="lms-dashboard-calendar">
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={false}
                    locales={allLocales}
                    locale={getCurrentLanguageCode()}
                    firstDay={1}
                    height="auto"
                    fixedWeekCount={false}
                    dayMaxEvents={3}
                    dayHeaderFormat={{ weekday: 'short' }}
                    events={events}
                    eventOrder="sortIndex"
                    eventContent={renderEvent}
                    eventClick={handleEventClick}
                    datesSet={(info) => onMonthChange(info.view.currentStart)}
                />
            </div>

            <OverlayPanel ref={overlayRef} style={{ maxWidth: '24rem' }}>
                {selected && (
                    <div className="flex flex-column gap-2">
                        <div className="flex align-items-center gap-2">
                            <span className="border-circle inline-block flex-shrink-0" style={{ width: '0.6rem', height: '0.6rem', backgroundColor: selectedStyle.dot }} />
                            <span className="font-bold" style={{ color: '#101828' }}>
                                {selected.trainingName}
                            </span>
                        </div>
                        <span className="text-sm mb-1" style={{ color: '#667085' }}>
                            {[selected.trainingCode, selected.trainingTypeText].filter(Boolean).join(' · ')}
                        </span>
                        <DetailRow icon="pi-calendar" label={th('lmsDashboard.calendar.date')} value={formatDateTimeRange(selected)} />
                        <DetailRow icon="pi-map-marker" label={th('lmsDashboard.calendar.location')} value={selected.planningLocationText || '-'} />
                        <DetailRow icon="pi-globe" label={th('lmsDashboard.calendar.language')} value={selected.trainingLanguage || '-'} />
                        <DetailRow icon="pi-user" label={th('lmsDashboard.calendar.instructors')} value={selected.instructorNames?.length ? selected.instructorNames.join(', ') : '-'} />
                        <DetailRow icon="pi-users" label={th('lmsDashboard.calendar.capacity')} value={selected.trainingCapacity ?? '-'} />
                        <DetailRow icon="pi-wallet" label={th('lmsDashboard.calendar.fee')} value={formatFee(selected.fee, selected.currency, locale)} />
                    </div>
                )}
            </OverlayPanel>
        </DashboardPanel>
    );
};

export default TrainingCalendar;
